/**
 * Migration utility to move templates from file-based storage to Firebase database
 * This script creates NDA and Professional Services templates in Firestore
 */

import type { DocumentTemplateField } from '../models/Document';
import { UserRole } from '../models/User';
import { useDocumentTemplates } from '../hooks/useDocumentTemplates';
import { useAuth } from '../contexts/AuthContext';
import {
  ndaTemplateContent,
  professionalServicesTemplateContent,
  saasProposalTemplateContent,
} from './templateContent';

/**
 * NDA Template Field Definitions
 * Based on placeholders found in nda-template.html
 */
export const ndaTemplateFields: DocumentTemplateField[] = [
  { key: 'effectiveDate', label: 'Effective Date', type: 'date', required: true },
  { key: 'recipientName', label: 'Recipient Name', type: 'text', required: true },
  { key: 'purposeOfDisclosure', label: 'Purpose for Disclosure', type: 'multiline', required: true },
  { key: 'jurisdiction', label: 'Governing Law Jurisdiction', type: 'text', required: true },
  { key: 'sentralSignerName', label: 'Sentral Group Signer Name', type: 'text', required: true },
  { key: 'sentralSignerTitle', label: 'Sentral Group Signer Title', type: 'text', required: true },
  { key: 'recipientCompanyName', label: 'Recipient Company Name', type: 'text', required: true },
  { key: 'recipientSignerName', label: 'Recipient Signer Name', type: 'text', required: true },
  { key: 'recipientSignerTitle', label: 'Recipient Signer Title', type: 'text', required: true },
];

/**
 * Professional Services Template Field Definitions
 * Based on placeholders found in professional-services-template.html
 */
export const professionalServicesTemplateFields: DocumentTemplateField[] = [
  { key: 'effectiveDate', label: 'Effective Date', type: 'date', required: true },
  { key: 'contractorAddress', label: 'Contractor Address', type: 'multiline', required: true },
  { key: 'contractorEmail', label: 'Contractor Email', type: 'text', required: true },
  { key: 'contractorPhone', label: 'Contractor Phone', type: 'text', required: false },
  { key: 'clientName', label: 'Client Name', type: 'client', required: true },
  { key: 'clientAddress', label: 'Client Address', type: 'multiline', required: true },
  { key: 'clientEmail', label: 'Client Email', type: 'client', required: true },
  { key: 'clientPhone', label: 'Client Phone', type: 'text', required: false },
  { key: 'additionalServices', label: 'Additional Services', type: 'multiline', required: false },
  { key: 'projectScope', label: 'Project Scope', type: 'multiline', required: true },
  { key: 'startDate', label: 'Start Date', type: 'date', required: true },
  { key: 'projectDuration', label: 'Project Duration', type: 'text', required: true },
  { key: 'meetingFrequency', label: 'Meeting Frequency', type: 'text', required: true },
  { key: 'communicationMethod', label: 'Communication Method', type: 'text', required: true },
  { key: 'projectManagementTool', label: 'Project Management Tool', type: 'text', required: true },
  { key: 'contractorSignerName', label: 'Contractor Signer Name', type: 'text', required: true },
  { key: 'contractorSignerTitle', label: 'Contractor Signer Title', type: 'text', required: true },
  { key: 'clientSignerName', label: 'Client Signer Name', type: 'text', required: true },
  { key: 'clientSignerTitle', label: 'Client Signer Title', type: 'text', required: true },
  { key: 'signatureDate', label: 'Signature Date', type: 'date', required: true },
];

/**
 * SaaS Proposal Template Field Definitions
 * Based on placeholders found in the SaaS proposal template
 */
export const saasProposalTemplateFields: DocumentTemplateField[] = [
  { key: 'proposalTitle', label: 'Proposal Title', type: 'text', required: true },
  { key: 'proposalDate', label: 'Proposal Date', type: 'date', required: true },
  { key: 'clientCompanyName', label: 'Client Company Name', type: 'text', required: true },
  { key: 'providerCompanyName', label: 'Provider Company Name', type: 'text', required: true },

  { key: 'productOrProjectName', label: 'Product / Project Name', type: 'text', required: true },
  { key: 'objective', label: 'Objective', type: 'multiline', required: true },
  { key: 'executiveSummary', label: 'Executive Summary', type: 'multiline', required: true },

  { key: 'platformsSummary', label: 'Platforms Summary', type: 'multiline', required: true },
  { key: 'coreFeatures', label: 'Core Features', type: 'multiline', required: true },
  { key: 'adminCapabilities', label: 'Admin Capabilities', type: 'multiline', required: true },
  { key: 'securityAndAccess', label: 'Security and Access', type: 'multiline', required: true },

  { key: 'outOfScope', label: 'Out of Scope', type: 'multiline', required: true },
  {
    key: 'deliveryTimeline',
    label: 'Delivery Timeline Details',
    type: 'multiline',
    required: true,
  },
  {
    key: 'thirdPartyApprovals',
    label: 'Third Party Approvals Note',
    type: 'multiline',
    required: true,
  },

  { key: 'lineItem1Name', label: 'Line Item 1 Name', type: 'text', required: true },
  { key: 'lineItem1Desc', label: 'Line Item 1 Description', type: 'multiline', required: true },
  { key: 'lineItem1Amount', label: 'Line Item 1 Amount', type: 'text', required: true },

  { key: 'lineItem2Name', label: 'Line Item 2 Name', type: 'text', required: false },
  { key: 'lineItem2Desc', label: 'Line Item 2 Description', type: 'multiline', required: false },
  { key: 'lineItem2Amount', label: 'Line Item 2 Amount', type: 'text', required: false },

  { key: 'lineItem3Name', label: 'Line Item 3 Name', type: 'text', required: false },
  { key: 'lineItem3Desc', label: 'Line Item 3 Description', type: 'multiline', required: false },
  { key: 'lineItem3Amount', label: 'Line Item 3 Amount', type: 'text', required: false },

  { key: 'subtotal', label: 'Subtotal', type: 'text', required: true },
  { key: 'gstRate', label: 'GST Rate', type: 'text', required: true },
  { key: 'gstAmount', label: 'GST Amount', type: 'text', required: true },
  { key: 'grandTotal', label: 'Grand Total', type: 'text', required: true },

  { key: 'paymentSchedule', label: 'Payment Schedule', type: 'multiline', required: true },
  {
    key: 'hourlyRate',
    label: 'Hourly Rate for Out-of-Scope',
    type: 'text',
    required: true,
  },

  {
    key: 'hostingNotes',
    label: 'Hosting Notes / Estimate',
    type: 'multiline',
    required: false,
  },
  { key: 'assumptions', label: 'Assumptions', type: 'multiline', required: false },

  { key: 'providerSignerName', label: 'Provider Signer Name', type: 'text', required: true },
  { key: 'providerSignerTitle', label: 'Provider Signer Title', type: 'text', required: true },

  { key: 'clientSignerName', label: 'Client Signer Name', type: 'text', required: true },
  { key: 'clientSignerTitle', label: 'Client Signer Title', type: 'text', required: true },
];

/**
 * Migration data for both templates
 */
export const templateMigrationData = {
  nda: {
    name: 'Non-Disclosure Agreement',
    description: 'Standard NDA template for protecting confidential information',
    content: ndaTemplateContent,
    dynamicFields: ndaTemplateFields,
    templatePath: 'src/assets/templates/nda-template.html', // For reference
  },
  professionalServices: {
    name: 'Professional Services Agreement',
    description: 'Standard Professional Services Agreement template for client engagements',
    content: professionalServicesTemplateContent,
    dynamicFields: professionalServicesTemplateFields,
    templatePath: 'src/assets/templates/professional-services-template.html', // For reference
  },
  saasProposal: {
    name: 'SaaS Proposal Template',
    description: 'Standard SaaS proposal template with editable scope, pricing, and signature blocks.',
    content: saasProposalTemplateContent,
    dynamicFields: saasProposalTemplateFields,
    templatePath: 'src/assets/templates/saas-proposal-template.html', // For reference
  },
};

/**
 * Hook to migrate templates to database
 * Supports migrating individual templates or all at once,
 * and exposes which templates already exist for the current organization.
 */
export function useTemplateMigration() {
  const { organizationId, currentUser } = useAuth();
  const { templates, createTemplate } = useDocumentTemplates(organizationId);

  const checkExisting = () => {
    const byName = (name: string) =>
      templates.some((t) => t.name === name && t.organizationId === organizationId);

    return {
      ndaExists: byName(templateMigrationData.nda.name),
      professionalServicesExists: byName(templateMigrationData.professionalServices.name),
      saasProposalExists: byName(templateMigrationData.saasProposal.name),
    };
  };

  const ensureOrg = () => {
    if (organizationId) {
      return organizationId;
    }

    // Allow SUPER_ADMINs to migrate templates even without an assigned organization
    // by storing them under a global organization scope.
    if (currentUser?.role === UserRole.SUPER_ADMIN) {
      return 'global';
    }

    throw new Error(
      'Organization ID is required. Please ensure you are logged in and have an organization assigned.'
    );
  };

  const migrateNda = async () => {
    const orgId = ensureOrg();
    console.log('Migrating NDA template...');
    const id = await createTemplate({
      name: templateMigrationData.nda.name,
      description: templateMigrationData.nda.description,
      content: templateMigrationData.nda.content,
      templatePath: templateMigrationData.nda.templatePath,
      dynamicFields: templateMigrationData.nda.dynamicFields,
      isActive: true,
      organizationId: orgId,
    });
    console.log(`✓ NDA template created with ID: ${id}`);
    return id;
  };

  const migrateProfessionalServices = async () => {
    const orgId = ensureOrg();
    console.log('Migrating Professional Services template...');
    const id = await createTemplate({
      name: templateMigrationData.professionalServices.name,
      description: templateMigrationData.professionalServices.description,
      content: templateMigrationData.professionalServices.content,
      templatePath: templateMigrationData.professionalServices.templatePath,
      dynamicFields: templateMigrationData.professionalServices.dynamicFields,
      isActive: true,
      organizationId: orgId,
    });
    console.log(`✓ Professional Services template created with ID: ${id}`);
    return id;
  };

  const migrateSaasProposal = async () => {
    const orgId = ensureOrg();
    console.log('Migrating SaaS Proposal template...');
    const id = await createTemplate({
      name: templateMigrationData.saasProposal.name,
      description: templateMigrationData.saasProposal.description,
      content: templateMigrationData.saasProposal.content,
      templatePath: templateMigrationData.saasProposal.templatePath,
      dynamicFields: templateMigrationData.saasProposal.dynamicFields,
      isActive: true,
      organizationId: orgId,
    });
    console.log(`✓ SaaS Proposal template created with ID: ${id}`);
    return id;
  };

  const migrateAll = async (): Promise<{
    ndaId?: string;
    professionalServicesId?: string;
    saasProposalId?: string;
    errors: string[];
  }> => {
    const errors: string[] = [];
    const result: {
      ndaId?: string;
      professionalServicesId?: string;
      saasProposalId?: string;
      errors: string[];
    } = { errors };

    try {
      result.ndaId = await migrateNda();
    } catch (error: any) {
      const errorMsg = `Failed to create NDA template: ${error.message}`;
      console.error(errorMsg, error);
      errors.push(errorMsg);
    }

    try {
      result.professionalServicesId = await migrateProfessionalServices();
    } catch (error: any) {
      const errorMsg = `Failed to create Professional Services template: ${error.message}`;
      console.error(errorMsg, error);
      errors.push(errorMsg);
    }

    try {
      result.saasProposalId = await migrateSaasProposal();
    } catch (error: any) {
      const errorMsg = `Failed to create SaaS Proposal template: ${error.message}`;
      console.error(errorMsg, error);
      errors.push(errorMsg);
    }

    if (errors.length === 0) {
      console.log('✓ All templates migrated successfully!');
    } else {
      console.warn('⚠ Some templates failed to migrate:', errors);
    }

    return result;
  };

  return {
    migrateAll,
    migrateNda,
    migrateProfessionalServices,
    migrateSaasProposal,
    checkExisting,
  };
}
